import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Input, message, Table, Tooltip } from 'antd'
import { FaPlus, FaEye, FaUpload, FaEdit, FaTrashAlt, FaSyncAlt, FaHistory } from 'react-icons/fa'
import CustomModal from '../../Components/CustomModal/CustomModal'
import { GtfsUpload, GtfsVersionsModal, SearchOrganizations, DeleteModal } from '../../Components'
import { GET, POST } from '../../utils/apis'
import { secureApi } from '../../Config/api'
import { fetchAllOrganization, formatDate, verifyToken } from '../../utils/helpers'
import { allPaths, translations } from '../../utils/constants'
import { setOrganizationFilter } from '../../Redux/actions/organizationActions'
import { DatabaseZap, Search, Building2 } from 'lucide-react'

const initials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '-'

const countChip = (value) => (
  <span className={`vl-count-chip${!Number(value) ? ' vl-count-chip-zero' : ''}`}>{value ?? 0}</span>
)

const Gtfs = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(state => state?.authReducer?.user)
  const organizationFromRedux = useSelector((state) => state.organizationReducer?.allOrganizations)
  const organizationFilter = useSelector(state => state.organizationReducer?.organizationFilter)
  const organizationCount = useSelector(state => state.organizationReducer?.orgSelectionCount || 0)
  const [feeds, setFeeds] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [gtfsModal, setGtfsModal] = useState(false)
  const [searchModal, setSearchModal] = useState(false)
  const [nameModal, setNameModal] = useState(false)
  const [nameTarget, setNameTarget] = useState(null)
  const [nameValue, setNameValue] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [feedToDelete, setFeedToDelete] = useState(null)
  const [replaceModal, setReplaceModal] = useState(false)
  const [feedToReplace, setFeedToReplace] = useState(null)
  const [versionsModal, setVersionsModal] = useState(false)
  const [versionsFeed, setVersionsFeed] = useState(null)

  useEffect(() => {
    verifyToken(history, dispatch)
    fetchAllOrganization({ dispatch })
    fetchFeeds()
  }, [])

  const fetchFeeds = async () => {
    setLoading(true)
    try {
      const response = await secureApi?.get(GET?.GET_GTFS_FEEDS)
      if (response?.success) {
        setFeeds(response?.data || [])
      } else {
        message.error(response?.message || 'Failed to fetch GTFS feeds.')
      }
    } catch (error) {
      message.error('Error fetching GTFS feeds.')
    } finally {
      setLoading(false)
    }
  }

  const viewDetails = (record) => {
    history.push({
      pathname: `${allPaths?.GTFS_DETAILS}/${encodeURIComponent(record?.feed_source)}`,
      state: { feedName: record?.feed_name },
    })
  }

  const openNameModal = (record) => {
    setNameTarget(record)
    setNameValue(record?.feed_name || '')
    setNameModal(true)
  }

  const handleSaveName = async () => {
    setNameSaving(true)
    try {
      const response = await secureApi?.post(POST?.UPDATE_GTFS_FEED_NAME, {
        feed_source: nameTarget?.feed_source,
        feed_name: nameValue?.trim() || null,
        organization_id: nameTarget?.organization_id,
      })
      if (response?.success) {
        message.success(response?.message || 'GTFS feed name updated')
        setNameModal(false)
        fetchFeeds()
      } else {
        message.error(response?.message || 'Failed to update GTFS name.')
      }
    } catch (error) {
      message.error('Error updating GTFS name.')
    } finally {
      setNameSaving(false)
    }
  }

  const handleOrganizationChange = (value) => {
    dispatch(setOrganizationFilter(value))
  }

  const openReplaceModal = (record) => {
    setFeedToReplace(record)
    setReplaceModal(true)
  }

  const openVersionsModal = (record) => {
    setVersionsFeed(record)
    setVersionsModal(true)
  }

  const viewVersion = (version) => {
    setVersionsModal(false)
    setVersionsFeed(null)
    history.push({
      pathname: `${allPaths?.GTFS_DETAILS}/${encodeURIComponent(version?.feed_source)}`,
      state: { feedName: versionsFeed?.feed_name || version?.feed_name },
    })
  }

  const handleDelete = (record) => {
    setFeedToDelete(record)
    setDeleteModal(true)
  }

  const handleDeleteCancel = () => {
    setDeleteModal(false)
    setFeedToDelete(null)
  }

  const handleConfirmDelete = async () => {
    setDeleteModal(false)
    try {
      const response = await secureApi?.post(POST?.DELETE_GTFS_FEED, {
        feed_source: feedToDelete?.feed_source,
        organization_id: feedToDelete?.organization_id,
      })
      if (response?.success) {
        message.success(translations[language]?.deletedSuccess)
        fetchFeeds()
      } else {
        message.error(response?.message || 'Failed to delete GTFS feed.')
      }
    } catch (error) {
      message.error('Error deleting GTFS feed.')
    } finally {
      setFeedToDelete(null)
    }
  }

  const organizationName = (orgId) =>
    organizationFromRedux?.find((org) => org?.id === orgId)?.organization_name || '-'

  const filteredFeeds = feeds
    ?.filter((feed) =>
      `${feed?.feed_name || ''} ${feed?.feed_source || ''}`
        .toLowerCase()
        ?.includes(searchQuery?.toLowerCase())
    )
    // superAdmin's organization picker (shared redux filter, same as the other
    // pages); an empty selection means "all organizations".
    ?.filter((feed) =>
      user?.role === 'superAdmin' && organizationFilter?.length
        ? organizationFilter.includes(feed?.organization_id)
        : true
    )

  const columns = [
    {
      title: 'GTFS',
      dataIndex: 'feed_source',
      key: 'feedSource',
      render: (text, record) => {
        const feedName = record?.feed_name || text
        return (
          <div className='vl-name-cell' onClick={() => viewDetails(record)}>
            <span className='vl-avatar'>{initials(feedName)}</span>
            <span className='vl-name-text capitalized-name'>{feedName}</span>
            <FaEye className='vl-name-view' />
          </div>
        )
      },
    },
    {
      title: translations[language]?.routes,
      dataIndex: 'routes',
      key: 'routes',
      render: (text) => countChip(text),
    },
    {
      title: translations[language]?.stops,
      dataIndex: 'stops',
      key: 'stops',
      render: (text) => countChip(text),
    },
    {
      title: translations[language]?.attachedVehicles,
      dataIndex: 'attached_vehicles',
      key: 'attachedVehicles',
      render: (text) => countChip(text),
    },
    {
      title: translations[language]?.uploaded,
      dataIndex: 'updated_at',
      key: 'updatedAt',
      render: (text) => (text ? <span>{formatDate(text)}</span> : <span className='vl-muted'>-</span>),
    },
    ...(user?.role === 'superAdmin' ? [
      {
        title: translations[language]?.organizations,
        dataIndex: 'organization_id',
        key: 'organization',
        render: (text) => <span className='capitalized-name'>{organizationName(text)}</span>,
      }
    ] : []),
    {
      title: translations[language]?.action,
      key: 'action',
      width: 100,
      render: (_, record) => (
        <div className='vl-actions'>
          <Tooltip title={translations[language]?.editGtfsName}>
            <button
              type='button'
              className='vl-action-btn vl-action-edit'
              onClick={() => openNameModal(record)}
            >
              <FaEdit size={14} />
            </button>
          </Tooltip>
          {['admin', 'superAdmin'].includes(user?.role) && record?.versions > 0 && (
            <Tooltip title={`${translations[language]?.versions} (${record.versions})`}>
              <button
                type='button'
                className='vl-action-btn vl-action-view'
                onClick={() => openVersionsModal(record)}
                style={{ position: 'relative' }}
              >
                <FaHistory size={14} />
                <span
                  style={{
                    position: 'absolute',
                    top: -7,
                    right: -7,
                    background: '#722ed1',
                    color: '#fff',
                    borderRadius: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '0 4px',
                    lineHeight: '14px',
                  }}
                >
                  {record.versions}
                </span>
              </button>
            </Tooltip>
          )}
          {['admin', 'superAdmin'].includes(user?.role) && (
            <Tooltip title={translations[language]?.replaceGtfs}>
              <button
                type='button'
                className='vl-action-btn vl-action-view'
                onClick={() => openReplaceModal(record)}
              >
                <FaSyncAlt size={14} />
              </button>
            </Tooltip>
          )}
          {['admin', 'superAdmin'].includes(user?.role) && (
            <Tooltip title={translations[language]?.deleteGtfs}>
              <button
                type='button'
                className='vl-action-btn vl-action-delete'
                onClick={() => handleDelete(record)}
              >
                <FaTrashAlt size={15} />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <div className='insider-container-buttons report-header-div'>
          <h1 className='main-heading'>{translations[language]?.gtfs}</h1>
        </div>
        <div className='insider-container'>
          {feeds?.length > 0 && (
            <div className='insider-container-buttons'>
              <div className='vehicle-buttons-div'>
                {['admin', 'superAdmin'].includes(user?.role) && (
                  <button
                    className='add-button'
                    onClick={() => setGtfsModal(true)}
                  >
                    <FaUpload className='icons' />
                    {translations[language]?.addGtfs}
                  </button>
                )}
              </div>
              <div className='search-main-div'>
                <div className="search-inner-div">
                  <Search className="search-icon" />
                  <input
                    placeholder={translations[language]?.searchGtfs}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  {searchQuery && (
                    <span className="clear-icon" style={{ cursor: "pointer" }} onClick={() => setSearchQuery("")}>
                      ✕
                    </span>
                  )}
                </div>
                {user?.role === 'superAdmin' ? <div className='get-reports-container'>
                  <button
                    className={`add-button organization ${searchModal ? 'open-organization' : 'close-organization'}`}
                    onClick={() => setSearchModal(!searchModal)}
                  >
                    <Building2 />
                    {translations[language]?.organizations}
                    {organizationCount ? <div className="filter-count-badge">
                      {organizationCount}
                    </div> : null}
                  </button>
                </div> : null}
              </div>
            </div>
          )}
          <div className='vd-card'>
            {feeds?.length === 0 && !loading ? (
              <div className='vd-card-body'>
                <div className="no-vehicles-container">
                  <div className="no-vehicles-text-center">
                    <div className="flex-col-center">
                      <div className="no-vehicles-text-center">
                        <DatabaseZap className='no-vehicle-icon' />
                        <h3 className="no-vehicles-heading">{translations[language]?.noGtfsFound}</h3>
                        <p className="no-vehicles-description">
                          {translations[language]?.addGtfsToSee}
                        </p>
                        {['admin', 'superAdmin'].includes(user?.role) && (
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <buttons className='add-button' onClick={() => setGtfsModal(true)}>
                              <FaPlus className='icons' />
                              {translations[language]?.addGtfs}
                            </buttons>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='vd-card-header'>
                  <div className='vl-list-title'>
                    <h2 className='vd-card-title'>{translations[language]?.gtfsList}</h2>
                    <span className='vl-total-chip'>
                      {translations[language]?.totalRecord}: <strong>{filteredFeeds?.length}</strong>
                    </span>
                  </div>
                </div>
                <div className='vd-card-body'>
                  <Table
                    className='vl-table'
                    dataSource={filteredFeeds}
                    columns={columns}
                    rowKey='feed_source'
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isVisible={nameModal}
        onClose={() => setNameModal(false)}
        title={translations[language]?.editGtfsName}
        actionText={translations[language]?.save || 'Save'}
        onAction={handleSaveName}
        loading={nameSaving}
        language={language}
      >
        <div style={{ padding: '8px 0' }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {translations[language]?.gtfsName}
          </label>
          <Input
            className="inp-height-43"
            placeholder={nameTarget?.feed_source}
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            maxLength={100}
          />
        </div>
      </CustomModal>

      <DeleteModal
        title={translations[language]?.deleteGtfs}
        bodyText={
          <>
            {translations[language]?.areYouSureYouWantToDltThisGtfs}
            {feedToDelete?.feed_name ? ` (${feedToDelete.feed_name})` : ''}
          </>
        }
        isOpen={deleteModal}
        onCancel={handleDeleteCancel}
        onDelete={handleConfirmDelete}
        language={language}
      />

      <GtfsVersionsModal
        isVisible={versionsModal}
        onClose={() => {
          setVersionsModal(false)
          setVersionsFeed(null)
        }}
        feed={versionsFeed}
        language={language}
        onView={viewVersion}
      />

      <div className={`search-organization-main-div ${searchModal ? 'open' : ''}`}>
        <SearchOrganizations
          selectedOrganizations={organizationFilter}
          handleOrganizationChange={handleOrganizationChange}
          organizations={organizationFromRedux}
          setSearchModal={setSearchModal}
          selectedFilter={organizationFilter}
          searchModal={searchModal}
          language={language}
          searchType={"gtfs"}
        />
      </div>

      <GtfsUpload
        isVisible={gtfsModal}
        onClose={() => {
          setGtfsModal(false)
          fetchFeeds()
        }}
        language={language}
        user={user}
        organizations={organizationFromRedux || []}
      />

      <GtfsUpload
        isVisible={replaceModal}
        onClose={() => {
          setReplaceModal(false)
          setFeedToReplace(null)
          fetchFeeds()
        }}
        language={language}
        user={user}
        organizations={organizationFromRedux || []}
        targetFeed={feedToReplace}
      />
    </div>
  )
}

export default Gtfs
